import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import Zone from "@insite/client-framework/Components/Zone";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setInitialValues from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetInitialValues";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import {
    getCurrencies,
    getFulfillmentLabel,
    getIsPunchOutSession,
    getLanguages,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setCurrency from "@insite/client-framework/Store/Context/Handlers/SetCurrency";
import setLanguage from "@insite/client-framework/Store/Context/Handlers/SetLanguage";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getHeader, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import { getPageLinkByPageType, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import ShowInventoryToggle, { ShowInventoryToggleStyles } from "@insite/content-library/Components/ShowInventoryToggle";
import ShowPricingToggle, { ShowPricingToggleStyles } from "@insite/content-library/Components/ShowPricingToggle";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps, IconProps } from "@insite/mobius/Icon";
import Globe from "@insite/mobius/Icons/Globe";
import MapPin from "@insite/mobius/Icons/MapPin";
import Menu from "@insite/mobius/Icons/Menu";
import X from "@insite/mobius/Icons/X";
import PanelMenu, { PanelMenuPresentationProps } from "@insite/mobius/PanelMenu";
import PanelRow, { PanelRowPresentationProps } from "@insite/mobius/PanelMenu/PanelRow";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import omitSingle from "@insite/mobius/utilities/omitSingle";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import CarosulArrow from "@tIntermatic/Icons/CarosulArrow";
import Circle from "@tIntermatic/Icons/Circle";
import Region from "@tIntermatic/Icons/Region";
import { MappedLink } from "@tIntermatic/Overrides/Widgets/Header/MainNavigation";
import cloneDeep from "lodash/cloneDeep";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const headerLinkListLinkFields =
        (getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
            return widget.type === "Header/HeaderLinkList";
        })?.fields.links as LinkModel[]) ?? [];
    const displayPricingAndInventory = state.context.session?.displayPricingAndInventory || false;
    const currentMode = getCookie(NavigationModeCookieName) || "Storefront";

    return {
        currencies: getCurrencies(state),
        currentCurrencyId: state.context.session.currency?.id,
        currentCurrencySymbol: state.context.session.currency?.currencySymbol,
        languages: getLanguages(state),
        currentLanguage: state.context.session.language,
        currentWebsiteName: state.context.website.name,
        activelanguage: state.context.session.language?.description,
        currentLocation: getLocation(state),
        isSigningIn: state.context.isSigningIn,
        userName: state.context.session?.userName,
        isSalesPerson: state.context.session?.isSalesPerson,
        isGuest: state.context.session?.isGuest,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage"),
        headerLinkListLinkFields,
        showCustomerMenuItem: getSettingsCollection(state).accountSettings.enableWarehousePickup,
        fulfillmentLabel: getFulfillmentLabel(state),
        drawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
        isPunchOutSession: getIsPunchOutSession(state),
        // regions: getRegions(),
        homePageUrl: getPageLinkByPageType(state, "HomePage")?.url,
        visibilityState: findVisibilityState(state.data.pages.widgetsById),
        displayPricingAndInventoryToggles: displayPricingAndInventory && currentMode === "Storefront",
    };
};

const mapDispatchToProps = {
    setCurrency,
    setLanguage,
    signOut,
    cancelPunchOut,
    setInitialValues,
    setNavDrawerIsOpen,
};

interface OwnProps {
    links: MappedLink[];
    showQuickOrder: boolean;
    quickOrderLink: PageLinkModel | undefined;
    websiteLinks: MappedLink[];
    currentWebsite: string;
    displayVmiNavigation?: boolean;
    vmiPageLinks?: (PageLinkModel | undefined)[];
    showFlyoutPricingToggle?: boolean;
    showFlyoutInventoryToggle?: boolean;
    allowOpenParentLinks?: boolean;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface NavigationDrawerStyles {
    menuTriggerButton?: ButtonPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerSectionWrapper?: InjectableCss;
    currencySelectorWrapper?: InjectableCss;
    languageSelectorWrapper?: InjectableCss;
    panelMenu?: PanelMenuPresentationProps;
    panelSectionWrapper?: InjectableCss;
    menuRowIcon?: IconProps;
    userRowTypography?: TypographyPresentationProps;
    punchOutUserRowText?: TypographyPresentationProps;
    mainNavigationRow?: PanelRowPresentationProps;
    mainNavigationRowIcon?: IconProps;
    punchOutNavigationRowIcon?: IconProps;
    mainNavigationRowTypography?: TypographyPresentationProps;
    logoLinks?: PanelRowPresentationProps & { typographyProps: TypographyPresentationProps };
    currencySymbol?: TypographyPresentationProps;
    changeCustomerRow?: PanelRowPresentationProps;
    changeCustomerRowContainer?: GridContainerProps;
    fulfillmentMethodGridItem?: GridItemProps;
    addressesGridItem?: GridItemProps;
    pickUpAddressGridItem?: GridItemProps;
    applyButtonGridItem?: GridItemProps;
    signOutRow?: PanelRowPresentationProps;
    signOutRowText?: TypographyPresentationProps;
    regionIcon?: IconPresentationProps;
    showInventoryToggle?: ShowInventoryToggleStyles;
    showPricingToggle?: ShowPricingToggleStyles;
    mobileRegionSelector?: InjectableCss;
    iconElementEnabled?: IconPresentationProps;
    iconElementDisabled?: IconPresentationProps;
    regionSelector?: PanelRowPresentationProps & { typographyProps: TypographyPresentationProps };
    selectorMenu?: PanelMenuPresentationProps;
}

const StyledSection = getStyledWrapper("section");
const StyledSpan = getStyledWrapper("span");

export const navigationDrawerStyles: NavigationDrawerStyles = {
    menuTriggerButton: {
        shape: "pill",
        color: "secondary",
        size: 50,
    },
    drawer: {
        size: 300,
        cssOverrides: {
            drawerContent: css`
                margin-top: -2px;
                overflow: hidden;
                background-color: #fff !important;
                box-shadow: inset -10px 0 6px 0 rgb(0 0 0 / 14%);
                height: 100%;
                padding-bottom: 100px;
            `,
            drawerContainer: css`
                div[class^="ContentBody"] {
                    ${({ theme }) => {
                        return breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    overflow-y: hidden !important;
                                `,
                                css`
                                    overflow-y: hidden !important;
                                `,
                                css`
                                    overflow-y: hidden !important;
                                `,
                                null,
                            ],
                            "minMax",
                        );
                    }}
                }
                &::after {
                    content: url("/UserFiles/Images/icons/Close.svg");
                    color: #fff;
                    position: absolute;
                    top: 8px;
                    left: 330px;
                    padding: 5px 0;
                    font-size: 25px;
                }
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            null,
                            css`
                                display: none;
                            `,
                            null,
                        ],
                        "min",
                    );
                }}
            `,
            scrim: css`
                background-color: transparent !important;
            `,
            drawerBody: css`
                #drawerTitle {
                    width: 96%;
                    box-shadow: 10px 0px 12px -6px;
                    min-height: 71px !important;
                    padding: 0;
                    background: #fff;
                    button {
                        background: transparent;
                        border: none;
                        > span[class^="IconWrapper"] {
                            color: #252525;
                            > svg {
                                stroke-width: 1.3 !important;
                            }
                        }
                    }
                }
            `,
        },
    },
    drawerSectionWrapper: {
        css: css`
            &:first-child {
                margin-top: 0;
                padding-bottom: 10px;
                background: #fff;
                width: 94%;
                > a[class^="StyledButton"] {
                    span[class^="TypographyStyle"] {
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    css`
                                        font-size: 16px;
                                        font-weight: 500;
                                    `,
                                    css`
                                        font-size: 16px;
                                        font-weight: 500;
                                    `,
                                    css`
                                        font-size: 16px;
                                        font-weight: 500;
                                    `,
                                    null,
                                ],
                                "minMax",
                            )}
                    }
                }
            }
            &:last-child {
                margin-bottom: 5px;
            }
            margin-top: 20px;
        `,
    },
    panelMenu: {
        cssOverrides: {
            wrapper: css`
                > button {
                    width: calc(100% - 2px);
                    &:focus {
                        outline: none !important;
                        box-shadow: none !important;
                    }
                }
                span[class^="TypographyStyle"] {
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    font-size: 16px;
                                    font-weight: 500;
                                `,
                                css`
                                    font-size: 16px;
                                    font-weight: 500;
                                `,
                                css`
                                    font-size: 16px;
                                    font-weight: 500;
                                `,
                                null,
                            ],
                            "minMax",
                        )}
                }
                span[class^="sc"] {
                    svg {
                        height: 20px !important;
                        width: 20px !important;
                    }
                }
                span[class^="IconWrapper"] {
                    color: #252525;
                    > svg {
                        height: 16px;
                        width: 16px;
                        > g {
                            > g {
                                stroke-width: 2;
                            }
                        }
                    }
                }
                &:focus {
                    outline: none !important;
                    box-shadow: none !important;
                }
            `,
            children: css`
                div[class^="PanelRowStyle"] {
                    box-shadow: 8px 0px 12px -7px;
                    min-height: 71px !important;
                    padding: 0;
                    width: 96%;
                    background: #fff;
                    > button {
                        background: #fff !important;
                        border: 0 !important;
                        margin-bottom: 3px;
                        > span[class^="IconWrapper"] {
                            > svg {
                                height: 24px;
                                width: 24px;
                                stroke-width: 1.3 !important;
                            }
                        }
                    }
                }
                div[class^="PanelMenuStyle"] {
                    margin-top: -2px;
                    background: #fff !important;
                    box-shadow: inset -10px 0 6px 0 rgb(0 0 0 / 14%);
                    border-top: 1px solid #e5e5e5;
                    > a {
                        font-size: 16px;
                        font-weight: 600;
                        letter-spacing: 0;
                        line-height: 25px;
                        text-transform: uppercase;
                        border-bottom: 1px solid #e5e5e5;
                        background: #fff;
                        width: 94%;
                        padding: 13.5px 10px 13.5px 30px;
                        color: #252525;
                    }
                    > div {
                        > button {
                            width: calc(100% - 15px);
                            padding-left: 30px;
                            font-size: 16px;
                            font-weight: 600;
                            letter-spacing: 0;
                            line-height: 25px;
                            text-transform: uppercase;
                            border-bottom: 1px solid #e5e5e5;
                            background: #fff;
                            padding: 13.5px 10px 13.5px 30px;
                            color: #252525;
                        }
                    }
                }
            `,
        },
    },

    selectorMenu: {
        cssOverrides: {
            wrapper: css`
                > button {
                    width: calc(100% - 2px);
                    &:focus {
                        outline: none !important;
                        box-shadow: none !important;
                    }
                }
                span[class^="TypographyStyle"] {
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    font-size: 16px;
                                    font-weight: 500;
                                `,
                                css`
                                    font-size: 16px;
                                    font-weight: 500;
                                `,
                                css`
                                    font-size: 16px;
                                    font-weight: 500;
                                `,
                                null,
                            ],
                            "minMax",
                        )}
                }
                span[class^="IconWrapper"] {
                    color: #252525;
                    > svg {
                        height: 16px;
                        width: 16px;
                        > g {
                            > g {
                                stroke-width: 2;
                            }
                        }
                    }
                }

                &:focus {
                    outline: none !important;
                    box-shadow: none !important;
                }
            `,
            children: css`
                div[class^="PanelRowStyle"] {
                    box-shadow: 8px 0px 12px -7px;
                    min-height: 71px !important;
                    padding: 0;
                    width: 96%;
                    background: #fff;
                    > button {
                        background: #fff !important;
                        border: 0 !important;
                        margin-bottom: 3px;
                    }
                }
                a[class^="StyledButton"] {
                    font-size: 16px;
                    font-weight: 600;
                    letter-spacing: 0;
                    line-height: 25px;
                    text-transform: uppercase;
                    border-bottom: 1px solid #e5e5e5;
                    background: #fff;
                    padding: 13.5px 10px 13.5px 30px;
                    width: 100% !important;
                }
                button[class^="StyledButton"] {
                    font-size: 16px;
                    font-weight: 600;
                    letter-spacing: 0;
                    line-height: 25px;
                    text-transform: uppercase;
                    border-bottom: 1px solid #e5e5e5 !important;
                    background: #fff;
                    color: #252525;
                    width: 100% !important;
                }

                div[class^="PanelMenuStyle"] {
                    margin-top: -2px;
                    background: #fff !important;
                    box-shadow: inset -10px 0 6px 0 rgb(0 0 0 / 14%);
                    border-top: 1px solid #e5e5e5;
                }
            `,
        },
    },

    panelSectionWrapper: {
        css: css`
            height: 22px;
            display: flex;
            align-items: center;
            color: #252525;
            font-size: 16px;
            font-weight: 600;
            letter-spacing: 0;
            line-height: 50px;
        `,
    },
    menuRowIcon: {
        size: 22,
        css: css`
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    userRowTypography: {
        transform: "uppercase",
        color: "#252525",
        css: css`
            text-overflow: ellipsis;
            overflow: hidden;
            font-weight: 500 !important;
        `,
    },
    punchOutUserRowText: {
        transform: "uppercase",
        color: "#252525",
    },
    mainNavigationRow: {
        color: "transparent",
        moreIconProps: {
            src: CarosulArrow,
        },
        css: css`
            margin: 0px;
            border-top: 1px solid #e5e5e5;
            padding: 13.5px 10px 13.5px 30px;
            background: #fff !important;
        `,
    },
    mainNavigationRowIcon: {
        src: "User",
        color: "#252525",
        size: 22,
        css: css`
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            height: 36px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
            svg {
                width: 20px;
                height: 20px;
            }
        `,
    },
    punchOutNavigationRowIcon: {
        src: "User",
        color: "#252525",
        size: 22,
        css: css`
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    mainNavigationRowTypography: {
        transform: "uppercase",
        color: "#252525",
        weight: 600,
        css: css`
            display: flex !important;
        `,
    },
    logoLinks: {
        color: "common.accent",
        typographyProps: {
            ellipsis: true,
            transform: "uppercase",
        },
        moreIconProps: {
            src: CarosulArrow,
        },
        css: css`
            margin: 2px;
            background: #ffffff;
            width: calc(100% - 23px) !important;
            padding-left: 30px;
            border-bottom: 1px solid #e5e5e5 !important;
        `,
    },

    regionSelector: {
        color: "common.accent",
        typographyProps: {
            ellipsis: true,
            transform: "uppercase",
        },
        moreIconProps: {
            src: CarosulArrow,
        },
        css: css`
            margin: 2px;
            background: #ffffff;
            width: calc(100% - 23px) !important;
            padding-left: 30px;
            border-bottom: 1px solid #e5e5e5 !important;
            border-top: 1px solid #e5e5e5 !important;
        `,
    },
    currencySymbol: {
        size: 22,
        css: css`
            margin-top: -4px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    changeCustomerRow: {
        css: css`
            &:focus {
                outline: none;
            }
        `,
    },
    changeCustomerRowContainer: { gap: 15 },
    fulfillmentMethodGridItem: { width: 12 },
    addressesGridItem: { width: 12 },
    pickUpAddressGridItem: { width: 12 },
    applyButtonGridItem: { width: 12 },
    signOutRowText: {
        transform: "uppercase",
    },

    regionIcon: {
        size: 22,
        src: Region,
        css: css`
            height: 20px;
            width: 20px;
            margin-right: 5px;
            > svg {
                > g {
                    > g[id^="Font_Home_Default"] {
                        fill: #000;
                    }
                }
            }
        `,
    },

    iconElementEnabled: {
        css: css`
            height: 25px;
            width: 25px;
            justify-content: center;
            > svg {
                fill: #006bc0;
                stroke: #006bc0;
                height: 7px;
                width: 19px;
            }
        `,
    },
    iconElementDisabled: {
        css: css`
            height: 25px;
            width: 25px;
            justify-content: center;
            opacity: 0;
            > svg {
                fill: #006bc0;
                stroke: #006bc0;
                height: 7px;
                width: 19px;
            }
        `,
    },
    signOutRow: {
        css: css`
            background: none !important;
            padding-left: 30px;
            span {
                font-size: 16px !important;
                font-weight: 500 !important;
            }
        `,
    },
    showInventoryToggle: {
        toggleClickable: {
            css: css`
                text-transform: uppercase;
                padding: 10px 10px 10px 20px;
            `,
        },
    },
    showPricingToggle: {
        toggleClickable: {
            css: css`
                text-transform: uppercase;
                padding: 10px 10px 10px 20px;
            `,
        },
    },
};

const styles = navigationDrawerStyles;

const NavigationDrawer: FC<Props> = props => {
    const headerLinkListLinks = useGetLinks(props.headerLinkListLinkFields, o => o.fields.destination);
    const openDrawer = () => {
        props.setNavDrawerIsOpen({ navDrawerIsOpen: true });

        setTimeout(() => {
            const style = document.body.style;
            style.position = "fixed";
        }, 1000);
        props.setInitialValues({});
    };

    const closeDrawer = () => {
        props.setNavDrawerIsOpen({ navDrawerIsOpen: false });
        setTimeout(() => props.setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
    };

    const onSignOutHandler = () => {
        props.setNavDrawerIsOpen({ navDrawerIsOpen: false });
        props.isPunchOutSession ? props.cancelPunchOut() : props.signOut();
    };

    const changeLanguage = (id: string) => {
        props.setLanguage({ languageId: id });
        const returnUrl = currentLocation.pathname;
        if (
            returnUrl.includes("/Cart") ||
            returnUrl.includes("/Checkout") ||
            returnUrl.includes("/OrderConfirmation") ||
            returnUrl.includes("/MyAccount") ||
            returnUrl.includes("/SignIn") ||
            returnUrl.includes("/ChangePassword")
        ) {
            props.history.push(`${props.homePageUrl}`);
        }
        closeDrawer;
    };

    const {
        quickOrderLink,
        links,
        showQuickOrder,
        currencies,
        currentCurrencyId,
        currentCurrencySymbol,
        languages,
        currentLanguage,
        setLanguage,
        setCurrency,
        userName,
        isGuest,
        myAccountPageLink,
        signInUrl,
        currentLocation,
        showCustomerMenuItem,
        fulfillmentLabel,
        isPunchOutSession,
        headerLinkListLinkFields,
        websiteLinks,
        currentWebsite,
        displayVmiNavigation,
        vmiPageLinks,
        currentWebsiteName,
        displayPricingAndInventoryToggles,
        showFlyoutPricingToggle,
        showFlyoutInventoryToggle,
        allowOpenParentLinks,
        visibilityState,
        isSalesPerson,
    } = props;

    const showIcon = visibilityState === "both" || visibilityState === "icon";
    const showLabel = visibilityState === "both" || visibilityState === "label";

    const currentPageUrl = currentLocation.pathname;
    const updatedDefaultSearchPageLinks = cloneDeep(myAccountPageLink);
    const updatedMyAccountPageLinks: any = updatedDefaultSearchPageLinks?.children?.filter(
        item => item.title !== "Order Approval" && !(item.title === "Change Customer" && !isSalesPerson),
    );
    return (
        <>
            <Button onClick={openDrawer} {...styles.menuTriggerButton} data-test-selector="expandMobileMenu">
                <ButtonIcon src={Menu} />
                <VisuallyHidden>{translate("menu")}</VisuallyHidden>
            </Button>
            <Drawer
                draggable
                position="left"
                {...styles.drawer}
                isOpen={props.drawerIsOpen}
                handleClose={closeDrawer}
                contentLabel="menu drawer"
            >
                <StyledSection {...styles.drawerSectionWrapper}>
                    {currentWebsiteName.toLowerCase() === "main" &&
                        currentLanguage?.languageCode.toLowerCase() === "en" && (
                            <StyledWrapper>
                                {userName && !isGuest && !isPunchOutSession ? (
                                    <PanelMenu
                                        currentUrl={currentPageUrl}
                                        panelTrigger={
                                            <PanelRow hasChildren {...styles.mainNavigationRow}>
                                                <StyledSpan {...styles.panelSectionWrapper}>
                                                    <Icon {...styles.mainNavigationRowIcon} />
                                                    <Typography {...styles.userRowTypography}>{userName}</Typography>
                                                </StyledSpan>
                                            </PanelRow>
                                        }
                                        menuItems={updatedMyAccountPageLinks || []}
                                        maxDepth={3}
                                        closeOverlay={closeDrawer}
                                        layer={0}
                                        {...styles.panelMenu}
                                    />
                                ) : isPunchOutSession ? (
                                    <PanelRow {...styles.mainNavigationRow}>
                                        <StyledSpan {...styles.panelSectionWrapper}>
                                            <Icon {...styles.punchOutNavigationRowIcon} />
                                            <Typography {...styles.punchOutUserRowText}>{userName}</Typography>
                                        </StyledSpan>
                                    </PanelRow>
                                ) : (
                                    <PanelRow
                                        {...styles.mainNavigationRow}
                                        isCurrent={currentPageUrl === signInUrl?.url}
                                        onClick={closeDrawer}
                                        href={signInUrl?.url}
                                    >
                                        <StyledSpan {...styles.panelSectionWrapper}>
                                            <Icon
                                                className="main-navigation-row-icon"
                                                {...styles.mainNavigationRowIcon}
                                            />
                                            <Typography className="user-row-text" {...styles.userRowTypography}>
                                                {translate("Sign In")}
                                            </Typography>
                                        </StyledSpan>
                                    </PanelRow>
                                )}
                            </StyledWrapper>
                        )}
                    {/* covers MainNavigation functionality */}
                    {!displayVmiNavigation &&
                        links.map((link, index) => {
                            if (link.children && link.children.length > 0) {
                                return (
                                    <PanelMenu
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={index}
                                        currentUrl={currentPageUrl}
                                        closeOverlay={closeDrawer}
                                        panelTrigger={
                                            <PanelRow hasChildren {...styles.mainNavigationRow}>
                                                <Typography {...styles.mainNavigationRowTypography}>
                                                    {link.title}
                                                </Typography>
                                            </PanelRow>
                                        }
                                        maxDepth={link.maxDepth || 3}
                                        layer={0}
                                        {...styles.panelMenu}
                                    >
                                        {link.children
                                            .filter(item => !item.excludeFromNavigation)
                                            .map(item => {
                                                const renderChildren = item.children?.filter(
                                                    child => !child.excludeFromNavigation,
                                                ).length;
                                                const menuItem = (
                                                    <PanelRow
                                                        // eslint-disable-next-line react/no-array-index-key
                                                        key={index}
                                                        href={item.url}
                                                        target={item.openInNewWindow ? "_blank" : ""}
                                                        {...styles.mainNavigationRow}
                                                        onClick={closeDrawer}
                                                    >
                                                        <Typography {...styles.mainNavigationRowTypography}>
                                                            {item.title}
                                                        </Typography>
                                                    </PanelRow>
                                                );
                                                if (renderChildren) {
                                                    return (
                                                        <PanelMenu
                                                            {...props}
                                                            as="div"
                                                            key={item.title}
                                                            panelTrigger={menuItem}
                                                            menuItems={item.children as MappedLink[]}
                                                            maxDepth={item.maxDepth || 3}
                                                            layer={1}
                                                            onClick={closeDrawer}
                                                        />
                                                    );
                                                }
                                                return menuItem;
                                            })}
                                        {link.shopAllProduct && (
                                            <PanelRow
                                                // eslint-disable-next-line react/no-array-index-key
                                                key={index}
                                                href={link.url}
                                                target={link.openInNewWindow ? "_blank" : ""}
                                                {...styles.mainNavigationRow}
                                                onClick={closeDrawer}
                                            >
                                                <Typography {...styles.mainNavigationRowTypography}>
                                                    {link.shopAllProductLabel
                                                        ? `${link.shopAllProductLabel} ${link.title}`
                                                        : `Shop All ${link.title}`}
                                                </Typography>
                                            </PanelRow>
                                        )}
                                    </PanelMenu>
                                );
                            }
                            return (
                                <PanelRow
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={index}
                                    href={link.url}
                                    target={link.openInNewWindow ? "_blank" : ""}
                                    {...styles.mainNavigationRow}
                                    onClick={closeDrawer}
                                >
                                    <Typography {...styles.mainNavigationRowTypography}>{link.title}</Typography>
                                </PanelRow>
                            );
                        })}
                    {!displayVmiNavigation && showQuickOrder && quickOrderLink && (
                        <PanelRow
                            isCurrent={currentPageUrl === quickOrderLink.url}
                            onClick={closeDrawer}
                            href={quickOrderLink.url}
                            {...styles.mainNavigationRow}
                        >
                            <Typography {...styles.mainNavigationRowTypography}>{quickOrderLink.title}</Typography>
                        </PanelRow>
                    )}
                    {displayVmiNavigation &&
                        vmiPageLinks &&
                        vmiPageLinks.map(
                            link =>
                                link && (
                                    <PanelRow
                                        key={link.id}
                                        isCurrent={currentPageUrl === link.url}
                                        onClick={closeDrawer}
                                        href={link.url}
                                        {...styles.mainNavigationRow}
                                    >
                                        <Typography {...styles.mainNavigationRowTypography}>{link.title}</Typography>
                                    </PanelRow>
                                ),
                        )}
                </StyledSection>
                {/* {this.props.headerLinkListLinks.length > 0 && (
                        <StyledSection {...styles.drawerSectionWrapper}>
                            {this.props.headerLinkListLinks.map(link => (
                                <PanelRow
                                    key={link.title}
                                    {...(styles.logoLinks && omitSingle(styles.logoLinks, "typographyProps"))}
                                    isCurrent={currentPageUrl === link.url}
                                    onClick={this.closeDrawer}
                                    href={link.url}
                                    target={link.openInNewWindow ? "_blank" : ""}
                                >
                                    <Typography {...styles.logoLinks?.typographyProps}>{link.title}</Typography>
                                </PanelRow>
                            ))}
                        </StyledSection>
                    )} */}
                <StyledSection {...styles.drawerSectionWrapper}>
                    {/* <SelectorMenu
                            options={currencies?.map(c => {
                                return {
                                    title: c.currencyCode.toUpperCase(),
                                    clickableProps: {
                                        onClick: () => {
                                            setCurrency({ currencyId: c.id });
                                            this.closeDrawer();
                                        },
                                    },
                                };
                            })}
                            closeModal={this.closeDrawer}
                            currentOption={currencies?.find(c => c.id === currentCurrencyId)?.currencyCode}
                            currentOptionIcon={
                                <Typography {...styles.currencySymbol}>{currentCurrencySymbol}</Typography>
                            }
                        /> */}

                    <PanelMenu
                        {...styles.selectorMenu}
                        panelTrigger={
                            <PanelRow hasChildren id="regionSelector" {...styles.regionSelector}>
                                <Typography {...styles.mainNavigationRowTypography}>
                                    <Icon src={Region} {...styles.regionIcon} />
                                    {currentWebsite}
                                </Typography>
                            </PanelRow>
                        }
                        layer={0}
                        closeOverlay={closeDrawer}
                    >
                        {websiteLinks && websiteLinks.length > 0 && (
                            <StyledSection {...styles.drawerSectionWrapper}>
                                {websiteLinks.map(link => (
                                    <PanelRow
                                        key={link.title}
                                        {...(styles.logoLinks && omitSingle(styles.logoLinks, "typographyProps"))}
                                        isCurrent={currentPageUrl === link.url}
                                        onClick={closeDrawer}
                                        href={link.url}
                                        target={link.openInNewWindow ? "_blank" : ""}
                                    >
                                        <Typography {...styles.logoLinks?.typographyProps}>
                                            <Icon
                                                {...(currentWebsite === link.title
                                                    ? { ...styles.iconElementEnabled }
                                                    : { ...styles.iconElementDisabled })}
                                                src={Circle}
                                                size={6}
                                            />
                                            {link.title}
                                        </Typography>
                                    </PanelRow>
                                ))}
                            </StyledSection>
                        )}
                    </PanelMenu>

                    <PanelMenu
                        {...styles.selectorMenu}
                        panelTrigger={
                            <PanelRow hasChildren id="languageSelector" {...styles.logoLinks}>
                                <Typography {...styles.mainNavigationRowTypography}>
                                    {currentLanguage?.description}
                                </Typography>
                            </PanelRow>
                        }
                        layer={0}
                        closeOverlay={closeDrawer}
                    >
                        {languages && languages.length > 0 && (
                            <StyledSection {...styles.drawerSectionWrapper}>
                                {languages.map(ele => (
                                    <PanelRow key={ele.id} onClick={() => changeLanguage(ele.id)}>
                                        <Typography {...styles.logoLinks?.typographyProps}>
                                            <Icon
                                                {...(ele.description === currentLanguage?.description
                                                    ? { ...styles.iconElementEnabled }
                                                    : { ...styles.iconElementDisabled })}
                                                src={Circle}
                                                size={6}
                                            />
                                            {ele.description}
                                        </Typography>
                                    </PanelRow>
                                ))}
                            </StyledSection>
                        )}
                    </PanelMenu>

                    {/* {showCustomerMenuItem && (
                            <PanelMenu
                                {...styles.panelMenu}
                                panelTrigger={
                                    <PanelRow hasChildren id="subMenu" {...styles.logoLinks} color="common.accent">
                                        <Typography {...styles.logoLinks?.typographyProps}>
                                            <Icon src={MapPin} {...styles.menuRowIcon} />
                                            {fulfillmentLabel}
                                        </Typography>
                                    </PanelRow>
                                }
                                layer={0}
                                closeOverlay={this.closeDrawer}
                            >
                                {isPunchOutSession ? (
                                    <PanelRow tabIndex={-1} {...styles.changeCustomerRow}>
                                        {createWidgetElement("Header/AddressDrawerPunchOutCustomer", {
                                            fields: {},
                                        })}
                                    </PanelRow>
                                ) : (
                                    <PanelRow tabIndex={-1} {...styles.changeCustomerRow}>
                                        <GridContainer {...styles.changeCustomerRowContainer}>
                                            <GridItem {...styles.fulfillmentMethodGridItem}>
                                                {createWidgetElement("Header/AddressDrawerFulfillmentMethodSelector", {
                                                    fields: {},
                                                })}
                                            </GridItem>
                                            <GridItem {...styles.addressesGridItem}>
                                                {createWidgetElement("Header/AddressDrawerSelectCustomer", {
                                                    fields: {},
                                                })}
                                            </GridItem>
                                            <GridItem {...styles.pickUpAddressGridItem}>
                                                {createWidgetElement("Header/AddressDrawerPickUpLocationSelector", {
                                                    fields: {},
                                                })}
                                            </GridItem>
                                            <GridItem {...styles.applyButtonGridItem}>
                                                {createWidgetElement("Header/AddressDrawerApplyButton", { fields: {} })}
                                            </GridItem>
                                        </GridContainer>
                                    </PanelRow>
                                )}
                            </PanelMenu>
                        )} */}
                    {displayPricingAndInventoryToggles && (
                        <>
                            {showFlyoutInventoryToggle && (
                                <ShowInventoryToggle extendedStyles={styles.showInventoryToggle} />
                            )}
                            {showFlyoutPricingToggle && <ShowPricingToggle extendedStyles={styles.showPricingToggle} />}
                        </>
                    )}
                    {currentWebsiteName.toLowerCase() === "main" &&
                        currentLanguage?.languageCode.toLowerCase() === "en" &&
                        userName &&
                        !isGuest && (
                            <PanelRow onClick={onSignOutHandler} {...styles.signOutRow}>
                                <Typography {...styles.signOutRowText}>
                                    {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                                </Typography>
                            </PanelRow>
                        )}
                </StyledSection>
            </Drawer>
        </>
    );
};

interface SelectorMenuProps {
    options?: MappedLink[];
    closeModal?: () => void;
    currentOption: React.ReactNode;
    currentOptionIcon?: React.ReactNode;
    dataTestSelector?: string;
}

const SelectorMenu: React.FC<SelectorMenuProps> = ({
    options,
    closeModal,
    currentOption,
    currentOptionIcon,
    dataTestSelector,
}) => {
    if (!options || options.length <= 1) {
        return null;
    }

    const trigger = (
        <PanelRow hasChildren {...styles.logoLinks}>
            <StyledSpan {...styles.panelSectionWrapper}>
                {currentOptionIcon}
                <Typography
                    data-test-selector={`${dataTestSelector}_currentOption`}
                    {...styles.logoLinks?.typographyProps}
                >
                    {currentOption}
                </Typography>
            </StyledSpan>
        </PanelRow>
    );

    return (
        <PanelMenu
            id="menuItems"
            data-test-selector={dataTestSelector}
            closeOverlay={closeModal}
            panelTrigger={trigger}
            menuItems={options}
            maxDepth={1}
            layer={0}
            {...styles.panelMenu}
        />
    );
};

function findVisibilityState(data: any): string | undefined {
    for (const key in data) {
        if (data[key].type === "Common/HeaderSignIn") {
            return data[key].generalFields.visibilityState;
        }
    }
    return undefined;
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(NavigationDrawer));
